// Vue directive to add a tooltip to an element
// Uses the Popper's Tooltip.js, adapted for the ukit in application/uikit/uikit-tooltip
//
// Currently only supports the html boolean option, we can add more if needed
//
// Usage:
//   v-tooltip="'hardcoded text'"
//   v-tooltip="methodCallThatReturnsTooltipContent"
//   v-tooltip.html="methodThatReturnsHtml"
//   v-tooltip.placementRight=""
//   v-tooltip.once="methodThatReturnsHtml" to run it once, not on every componentUpdate
import type { Options } from 'tooltip.js'

import type { ObjectDirective, DirectiveBinding } from 'vue'
import { SfTime } from '@app/adapters/time'
import Tooltip from '@app/setup/tooltip/uikit-tooltip'
import type { TooltipSizeOptions } from '@app/shared/types/tooltip'

interface TooltipElement extends HTMLElement {
  tooltip?: Tooltip
}

const renderTooltip = (
  el: TooltipElement,
  binding: DirectiveBinding<string | (() => string)>
): Tooltip | undefined => {
  // Don't render tooltip when content is empty
  if (typeof binding.value === 'function') {
    if (binding.value() === '') {
      return undefined
    }
  } else if (binding.value === '') {
    return undefined
  }

  const options: Options = {
    title: binding.value,
    textLeft: binding.modifiers.textLeft,
  }

  if (binding.modifiers.html) {
    options.html = true
  }

  if (binding.modifiers.placementRight) {
    options.placement = 'right'
  }

  if (binding.modifiers.placementLeft) {
    options.placement = 'left'
  }

  if (binding.modifiers.placementBottom) {
    options.placement = 'bottom'
  }

  if (binding.modifiers.timestamp) {
    options.title = SfTime(
      typeof options.title === 'function' ? options.title() : options.title
    ).format('LLL')
  }

  const dataset = el.dataset

  if (dataset.tooltipSize) {
    options.size = dataset.tooltipSize as TooltipSizeOptions
  }

  const tooltip = new Tooltip(el, options)
  el.tooltip = tooltip

  return tooltip
}

const tooltipDirective: ObjectDirective<TooltipElement> = {
  beforeMount(el, binding: DirectiveBinding<string | (() => string)>) {
    renderTooltip(el, binding)
  },

  updated(el, binding: DirectiveBinding<string | (() => string)>) {
    // we can force directive to init only once, not update when 'parent' components gets new values
    if (binding.modifiers.once) {
      return undefined
    }

    // when directive gets updated -tooltip was not rerendering content, disposing it and creating again is the only solution for now
    const oldTooltip = el.tooltip

    if (oldTooltip) {
      oldTooltip.dispose()
    }

    // We have to check if tooltip was opened before or not
    const wasOpen = oldTooltip
      ? oldTooltip._isOpen || oldTooltip.__isOpening
      : false

    const tooltip = renderTooltip(el, binding)

    if (wasOpen && tooltip) {
      tooltip.show()
    }

    const args = binding?.arg as unknown as { force: boolean }

    if (args?.force && tooltip) {
      tooltip.show()
    }
  },

  unmounted(el) {
    // Dispose of the tooltip. Otherwise the tooltip ghost remains when it's element is removed from the dom
    const tooltip = el.tooltip

    // Sometimes we return early from renderTooltip (when there is no value), so before destroying element we have to check if it exists
    if (tooltip) tooltip.dispose()
  },
}

export default tooltipDirective
